import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { claimPayment, getPaymentStatus } from "@/lib/api";
import { useSpark } from "@/lib/store";
import { BrandHeader } from "@/components/BrandHeader";
import { PublicFooter } from "@/components/PublicFooter";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map(e => e?.msg).filter(Boolean).join(" ") : "Something went wrong. Please try again.");

export default function PaymentSuccess() {
  const nav = useNavigate();
  const { setSession, switchUser } = useSpark();
  const sessionId = useRef(new URLSearchParams(window.location.search).get("session_id") || "");
  const [status, setStatus] = useState(null);
  const [polls, setPolls] = useState(0);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Payment received — Spark Coaching"; }, []);
  useEffect(() => {
    if (!sessionId.current) return;
    if (status?.payment_status === "paid" || ["failed", "expired", "refunded"].includes(status?.payment_status) || polls > 10) return;
    const t = setTimeout(async () => {
      try { setStatus(await getPaymentStatus(sessionId.current)); } catch (e) { setError(errText(e?.response?.data?.detail)); }
      setPolls(p => p + 1);
    }, polls === 0 ? 0 : 2000);
    return () => clearTimeout(t);
  }, [polls, status]);

  const claim = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { user } = await claimPayment({ session_id: sessionId.current, password, name });
      setSession(user); switchUser(user.user_id);
      nav("/welcome-note", { replace: true });
    } catch (err) {
      setError(errText(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const paid = status?.payment_status === "paid";
  const failed = ["failed", "expired", "refunded"].includes(status?.payment_status);
  const timedOut = !paid && !failed && polls > 10;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader links={[]} />
      <main className="flex-1" style={{ background: "hsl(var(--spark-cool))" }}>
        <div className="spark-narrow py-20 sm:py-28">
          <div data-testid="payment-success-card" className="spark-card mx-auto max-w-xl px-7 py-10 sm:px-12 sm:py-14">
            <img src="/brand/monogram_black.png" alt="" aria-hidden="true" className="h-14 w-14" />
            {!sessionId.current && <p data-testid="payment-missing" className="mt-8 text-base">We couldn't find that payment. <Link to="/start" className="underline">Start again</Link>.</p>}

            {sessionId.current && !paid && !failed && !timedOut && (
              <div data-testid="payment-checking">
                <p className="spark-eyebrow-copper mt-8">One moment</p>
                <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Confirming your payment…</h1>
                <p className="mt-4 spark-voice text-lg" style={{ color: "hsl(var(--spark-ink-soft))" }}>This usually takes a second or two.</p>
              </div>
            )}

            {failed && (
              <div data-testid="payment-failed">
                <p className="spark-eyebrow mt-8">Payment {status.payment_status}</p>
                <h1 className="mt-3 text-3xl font-semibold tracking-tight">That payment didn't go through.</h1>
                <p className="mt-4 text-base" style={{ color: "hsl(var(--spark-ink-soft))" }}>No account was created. You can <Link to="/start" className="underline">try again</Link> whenever you like.</p>
              </div>
            )}

            {timedOut && (
              <div data-testid="payment-timeout">
                <h1 className="mt-8 text-3xl font-semibold tracking-tight">We're still waiting on Stripe.</h1>
                <p className="mt-4 text-base" style={{ color: "hsl(var(--spark-ink-soft))" }}>Your card may still be processing. Refresh this page in a minute — your payment reference is saved.</p>
                <button data-testid="payment-retry-btn" onClick={() => setPolls(0)} className="spark-btn-secondary mt-6">Check again</button>
              </div>
            )}

            {paid && status.claimed && (
              <div data-testid="payment-already-claimed">
                <p className="spark-eyebrow-copper mt-8">Payment received</p>
                <h1 className="mt-3 text-3xl font-semibold tracking-tight">Your account is ready.</h1>
                <p className="mt-4 text-base" style={{ color: "hsl(var(--spark-ink-soft))" }}>This payment has already been used to set up your account for <strong>{status.email}</strong>.</p>
                <Link to="/signin" data-testid="payment-signin-link" className="spark-btn-primary mt-7">Sign in</Link>
              </div>
            )}

            {paid && !status.claimed && (
              <form onSubmit={claim} data-testid="payment-claim-form">
                <p className="spark-eyebrow-copper mt-8">Payment received · A$99</p>
                <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Welcome to Spark.</h1>
                <p className="mt-4 spark-voice text-lg sm:text-xl leading-snug" style={{ color: "hsl(var(--spark-ink-soft))" }}>
                  Last step: choose a password for <strong className="font-semibold text-[#17171C]">{status.email}</strong> so you can come back any time.
                </p>
                <label className="spark-eyebrow block mt-8 mb-2" htmlFor="claim-name">Your first name (optional)</label>
                <input id="claim-name" data-testid="claim-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="So Spark knows what to call you"
                       className="w-full rounded-xl border bg-white px-4 py-3.5 text-base focus:outline-none focus:ring-2" style={{ borderColor: "hsl(var(--input))", "--tw-ring-color": "rgba(44,80,160,0.35)" }} />
                <label className="spark-eyebrow block mt-5 mb-2" htmlFor="claim-password">Choose a password</label>
                <input id="claim-password" data-testid="claim-password-input" type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters"
                       className="w-full rounded-xl border bg-white px-4 py-3.5 text-base focus:outline-none focus:ring-2" style={{ borderColor: "hsl(var(--input))", "--tw-ring-color": "rgba(44,80,160,0.35)" }} />
                <button data-testid="claim-submit-btn" disabled={busy || password.length < 8} className="spark-btn-primary mt-6 w-full sm:w-auto disabled:opacity-60">
                  {busy ? "Creating your account…" : "Create my account and begin"}
                </button>
                {error && <p data-testid="claim-error" className="mt-3 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}
              </form>
            )}
            {error && !paid && <p data-testid="payment-error" className="mt-4 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
